/**
 * Screenshot preparation before upload (TD §10 "Overlay upload", R7): shrink to at most 2048 px on the long side,
 * then JPEG (quality 0.85), or keep a small PNG as it is.
 */

/** Longest side of an uploaded screenshot in pixels. */
export const OVERLAY_MAX_PX = 2048;
/** A PNG this small that needs no shrinking is uploaded unchanged. */
const KEEP_PNG_BYTES = 1_500_000;

/** Loads an image file into an element (works for every format the browser can show). */
function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file isn't a picture this browser can open. Use a PNG or JPG screenshot."));
    };
    img.src = url;
  });
}

/**
 * Shrinks a screenshot to at most 2048 px on its long side and returns the image to upload:
 * the original file for a small PNG or JPEG that already fits, otherwise a JPEG at quality 0.85.
 * Throws an Error with a friendly message when the file isn't a picture.
 */
export async function downscaleImage(file: File): Promise<Blob> {
  const img = await loadImage(file);
  const w = img.naturalWidth, h = img.naturalHeight;
  if (!w || !h) throw new Error("That picture looks empty. Try another screenshot.");
  const f = Math.min(1, OVERLAY_MAX_PX / Math.max(w, h));
  if (f === 1 && ((file.type === 'image/png' && file.size <= KEEP_PNG_BYTES) || (file.type === 'image/jpeg' && file.size <= KEEP_PNG_BYTES))) {
    return file;
  }
  const cw = Math.max(1, Math.round(w * f)), ch = Math.max(1, Math.round(h * f));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const g = canvas.getContext('2d');
  if (!g) throw new Error("This browser can't prepare the picture.");
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, cw, ch);
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, 0, 0, cw, ch);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error("This browser can't prepare the picture.");
  return blob;
}
